import { TZDate } from '@date-fns/tz';
import { DatePicker as DatePickerBase } from 'antd';
import classNames from 'classnames';
import { endOfDay, isBefore } from 'date-fns';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DEFAULT_MIN_DATE } from 'constants/Date';
import useDatePickerFormat from 'hooks/useDatePickerFormat';
import dateFnsGenerateConfig from 'polyfill/dateFnsGenerateConfig';

import { humanize } from 'utils/global';

const DatePicker = DatePickerBase.generatePicker(dateFnsGenerateConfig);

/** @type {React.ForwardRefExoticComponent<import('antd').DatePickerProps & { placeholder?: string; showNow?: boolean | string } & React.RefAttributes<any>>} */
const FBDatePicker = React.forwardRef(({ placeholder, showNow = true, ...rest }, ref) => {
  const intl = useIntl();
  const { dateFormat } = useDatePickerFormat();

  const handleClickNow = React.useCallback(
    (event) => {
      event.preventDefault();
      rest.onChange(new TZDate(new Date(), showNow)); // overrides today default behavior to select timezoned now
    },
    [rest, showNow]
  );

  const shouldRenderFooter = typeof showNow === 'string' && showNow.length > 0;

  const renderToday = React.useCallback(() => {
    const now = new TZDate(new Date(), showNow);
    const isDisabled = Boolean(rest.disabledDate?.(now) || isBefore(endOfDay(now), rest.minDate));

    return (
      <ul className="ant-picker-ranges">
        <li className="ant-picker-now">
          <a
            href="/#"
            className={classNames('ant-picker-now-btn', { 'ant-picker-now-btn-disabled': isDisabled })}
            aria-disabled={isDisabled}
            onClick={isDisabled ? undefined : handleClickNow}
          >
            <FormattedMessage id="today" defaultMessage="Today" />
          </a>
        </li>
      </ul>
    );
  }, [handleClickNow, rest, showNow]);

  return (
    <DatePicker
      ref={ref}
      format={dateFormat}
      minDate={DEFAULT_MIN_DATE}
      placeholder={placeholder && intl.formatMessage({ id: placeholder, defaultMessage: humanize(placeholder) })}
      renderExtraFooter={shouldRenderFooter ? renderToday : undefined}
      showNow={typeof showNow === 'boolean' ? showNow : false}
      {...rest}
    />
  );
});

FBDatePicker.RangePicker = DatePicker.RangePicker;

export default FBDatePicker;
